import React, { useState, useEffect } from 'react';
import { 
    Users, Clock, Save, CheckCircle2, RefreshCw, 
    Info, Loader2
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, DB_PATH } from '../../../config/firebase';
import { sincronizarHeadcountClientes } from '../../../services/alterdataService';
import Button from '../../../components/Button';

export const HeadcountTab = ({ isDarkMode, currentUser, onNavigateToLogs }) => {
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [savingConfig, setSavingConfig] = useState(false);
    
    // Configurações
    const [enabled, setEnabled] = useState(true);
    const [intervalHours, setIntervalHours] = useState(24);
    const [lastRun, setLastRun] = useState(null);
    const [totalProcessed, setTotalProcessed] = useState(0);
    const [totalUpdated, setTotalUpdated] = useState(0);
    const [lastDurationMs, setLastDurationMs] = useState(0);
    const [executionStatus, setExecutionStatus] = useState('IDLE');

    // Execução Manual
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState(null);
    const [syncResult, setSyncResult] = useState(null);

    // Carregar configurações de headcount
    useEffect(() => {
        const carregarConfig = async () => {
            try {
                setLoadingConfig(true);
                const ref = doc(db, `${DB_PATH}hub_settings`, 'headcount_sync_config');
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const data = snap.data();
                    setEnabled(data.enabled !== false);
                    setIntervalHours(data.intervalHours || 24);
                    setLastRun(data.lastRun || null);
                    setTotalProcessed(data.totalProcessed || 0);
                    setTotalUpdated(data.totalUpdated || 0);
                    setLastDurationMs(data.duracaoMs || 0);
                    setExecutionStatus(data.status || 'IDLE');
                }
            } catch (err) {
                console.error("Erro ao carregar configurações de headcount:", err);
            } finally {
                setLoadingConfig(false);
            }
        };

        carregarConfig();
    }, []);

    // Salvar configurações
    const handleSaveConfig = async () => {
        try {
            setSavingConfig(true);
            const ref = doc(db, `${DB_PATH}hub_settings`, 'headcount_sync_config');
            const payload = {
                enabled,
                intervalHours: Number(intervalHours),
                updatedAt: new Date().toISOString(),
                updatedBy: currentUser?.email || 'Administrador'
            };
            await setDoc(ref, payload, { merge: true });
            alert("Configuração de periodicidade de Headcount salva com sucesso!");
        } catch (err) {
            console.error("Erro ao salvar periodicidade:", err);
            alert("Erro ao salvar: " + err.message);
        } finally {
            setSavingConfig(false);
        }
    };

    // Disparar sincronização manual
    const handleTriggerSync = async () => {
        if (!window.confirm("Deseja iniciar agora a sincronização manual de Headcount para todos os clientes ativos do apEx Clientes?")) {
            return;
        }

        setIsSyncing(true);
        setSyncProgress({ percent: 5, status: 'Iniciando varredura com Alterdata...' });
        setSyncResult(null);

        try {
            const result = await sincronizarHeadcountClientes({
                db,
                DB_PATH,
                currentUser,
                onProgress: (p) => setSyncProgress(p)
            });

            setSyncResult(result);
            setLastRun(result.dataHora);
            setTotalProcessed(result.totalClientesVerificados);
            setTotalUpdated(result.totalAtualizados);
            setLastDurationMs(result.duracaoMs);
            setExecutionStatus(result.status);
        } catch (err) {
            console.error("Falha ao sincronizar headcount:", err);
            alert("Erro na sincronização de headcount: " + err.message);
        } finally {
            setIsSyncing(false);
        }
    };

    const formatDataHora = (isoStr) => {
        if (!isoStr) return 'Nunca executada';
        try {
            const d = new Date(isoStr);
            if (!isNaN(d.getTime())) return d.toLocaleString('pt-BR');
        } catch {}
        return isoStr;
    };

    const themeCard = isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-gray-200';
    const themeInput = `h-9 text-xs rounded-lg border px-3 transition-colors ${
        isDarkMode ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
    }`;

    if (loadingConfig) {
        return (
            <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-400">
                <Loader2 className="animate-spin text-indigo-500" size={32} />
                <p className="text-xs font-bold">Carregando parâmetros de Headcount...</p>
            </div>
        );
    }

    return (
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
            
            {/* Cabeçalho */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-4 dark:border-slate-700">
                <div>
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Users className="text-teal-500" size={22} />
                        Rotina de Headcount (apEx Clientes)
                    </h3>
                    <p className="text-xs opacity-70 mt-1">
                        Sincroniza o número exato de colaboradores ativos de cada cliente cadastrado em <b>apEx Clientes</b> diretamente do Alterdata DP, atualizando o campo estatístico <code className="font-bold text-teal-400">estatisticasDp.totalFuncionariosAtivos</code> sem onerar o Firestore.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        onClick={handleTriggerSync}
                        disabled={isSyncing}
                        className="bg-teal-600 hover:bg-teal-700 text-white text-xs shadow-md"
                        icon={isSyncing ? Loader2 : RefreshCw}
                    >
                        {isSyncing ? 'Sincronizando...' : 'Sincronizar Headcount Manualmente Agora'}
                    </Button>
                </div>
            </div>

            {/* Progresso de Execução */}
            {isSyncing && syncProgress && (
                <div className="p-4 rounded-xl border border-teal-400 bg-teal-50 dark:bg-teal-900/30 dark:border-teal-700 animate-pulse">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-bold text-teal-700 dark:text-teal-300 flex items-center gap-2">
                            <Loader2 className="animate-spin" size={16} />
                            {syncProgress.status}
                        </span>
                        <span className="text-xs font-mono font-bold text-teal-600 dark:text-teal-400">
                            {syncProgress.percent || 0}%
                        </span>
                    </div>
                    <div className="w-full bg-teal-200 dark:bg-teal-950 rounded-full h-2 overflow-hidden">
                        <div 
                            className="bg-teal-600 h-2 transition-all duration-300"
                            style={{ width: `${syncProgress.percent || 5}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Resumo pós-execução recente */}
            {syncResult && (
                <div className={`p-4 rounded-xl border flex items-start justify-between gap-4 ${
                    syncResult.status === 'SUCESSO' 
                        ? (isDarkMode ? 'bg-green-900/20 border-green-700 text-green-300' : 'bg-green-50 border-green-200 text-green-800')
                        : (isDarkMode ? 'bg-orange-900/20 border-orange-700 text-orange-300' : 'bg-orange-50 border-orange-200 text-orange-800')
                }`}>
                    <div className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-green-500 shrink-0 mt-0.5" />
                        <div className="text-xs space-y-1">
                            <p className="font-bold text-sm">Headcount Atualizado com Sucesso!</p>
                            <p>
                                Clientes ativos verificados: <b>{syncResult.totalClientesVerificados}</b> | Headcount atualizado no banco: <b>{syncResult.totalAtualizados}</b>
                            </p>
                            <p>
                                Duração da rotina: <b>{(syncResult.duracaoMs / 1000).toFixed(1)} segundos</b>. Executado por: <b>{syncResult.executadoPor}</b>.
                            </p>
                        </div>
                    </div>
                    {onNavigateToLogs && (
                        <button 
                            onClick={onNavigateToLogs}
                            className="text-xs font-bold underline hover:opacity-80 shrink-0"
                        >
                            Ver Log Completo
                        </button>
                    )}
                </div>
            )}

            {/* Cards de Métricas e Monitoramento da Rotina */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={`p-4 rounded-xl border ${themeCard}`}>
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Status da Rotina</span>
                    <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase border ${
                            executionStatus === 'SUCESSO'
                                ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400'
                                : executionStatus === 'EXECUTANDO'
                                ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400'
                                : executionStatus === 'ERRO' || executionStatus === 'FALHA'
                                ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400'
                        }`}>
                            {executionStatus}
                        </span>
                    </div>
                </div>

                <div className={`p-4 rounded-xl border ${themeCard}`}>
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Última Execução</span>
                    <p className="text-sm font-bold mt-2 font-mono">
                        {formatDataHora(lastRun)}
                    </p>
                </div>

                <div className={`p-4 rounded-xl border ${themeCard}`}>
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Clientes com Headcount Ativo</span>
                    <p className="text-xl font-bold mt-1 text-teal-500 font-mono">
                        {totalUpdated} <span className="text-xs font-normal opacity-60 font-sans">de {totalProcessed}</span>
                    </p>
                </div>

                <div className={`p-4 rounded-xl border ${themeCard}`}>
                    <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Duração da Última Execução</span>
                    <p className="text-sm font-bold mt-2 font-mono">
                        {lastDurationMs > 0 ? `${(lastDurationMs / 1000).toFixed(1)}s` : '-'}
                    </p>
                </div>
            </div>

            {/* Painel de Parametrização da Periodicidade */}
            <div className={`p-6 rounded-2xl border ${themeCard} space-y-4`}>
                <div className="flex items-center gap-2 text-sm font-bold text-teal-500">
                    <Clock size={18} />
                    <h4>Configuração de Periodicidade de Sincronização</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2 opacity-70">
                            Status do Agendamento
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input 
                                type="checkbox"
                                checked={enabled}
                                onChange={e => setEnabled(e.target.checked)}
                                className="w-5 h-5 rounded text-teal-600 accent-teal-600"
                            />
                            <span className="text-xs font-bold">
                                {enabled ? 'Rotina de Headcount Ativa (Automática)' : 'Rotina Desativada'}
                            </span>
                        </label>
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-2 opacity-70">
                            Periodicidade de Execução
                        </label>
                        <select
                            value={intervalHours}
                            onChange={e => setIntervalHours(Number(e.target.value))}
                            className={`${themeInput} w-full font-bold`}
                        >
                            <option value={1}>A cada 1 hora</option>
                            <option value={6}>A cada 6 horas</option>
                            <option value={12}>A cada 12 horas</option>
                            <option value={24}>Diária - A cada 24 horas (Recomendado)</option>
                            <option value={48}>A cada 48 horas (2 dias)</option>
                            <option value={72}>A cada 72 horas (3 dias)</option>
                        </select>
                    </div>

                    <div>
                        <Button
                            onClick={handleSaveConfig}
                            disabled={savingConfig}
                            className="w-full bg-teal-600 hover:bg-teal-700 text-white text-xs h-9"
                            icon={savingConfig ? Loader2 : Save}
                        >
                            {savingConfig ? 'Gravando...' : 'Salvar Periodicidade'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Explicação de Arquitetura e Decisão de Engenharia */}
            <div className={`p-4 rounded-xl border ${themeCard} flex items-start gap-3`}>
                <Info size={18} className="text-teal-500 shrink-0 mt-0.5" />
                <div className="text-xs leading-relaxed space-y-1 opacity-90">
                    <p className="font-bold text-teal-400">
                        Por que os dados individuais de funcionários de clientes externos não são gravados no banco?
                    </p>
                    <p>
                        • <b>Performance e Otimização de Storage:</b> A base de clientes conta com centenas de empresas que possuem milhares de funcionários. Gravar cada colaborador individualmente sobrecarregaria desnecessariamente o Firestore.
                    </p>
                    <p>
                        • <b>Leitura Instantânea de Metadados:</b> A API Alterdata retorna o totalizador de colaboradores ativos instantaneamente através da propriedade <code className="font-bold">meta.totalResourceCount</code> via requisição ultra leve (<code className="font-bold">page[limit]=1</code>).
                    </p>
                    <p>
                        • <b>Dados Gravados no apEx Clientes:</b> Cada cliente recebe apenas o resumo em <code className="font-bold text-teal-400">clientes/&#123;id&#125;/estatisticasDp</code> contendo <code className="font-bold">&#123; totalFuncionariosAtivos, alterdataEmpresaId, atualizadoEm &#125;</code>.
                    </p>
                </div>
            </div>

        </div>
    );
};

export default HeadcountTab;
